import axios from 'axios';

// Backend URL configuration
// In production (Vercel), use REACT_APP_BACKEND_URL to point to Railway backend
// In development or Emergent preview, use same origin
const getBackendUrl = () => {
  // Always prefer REACT_APP_BACKEND_URL if set
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  // Fallback to same origin (works in Emergent preview)
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return '';
};

const BACKEND_URL = getBackendUrl();
const API_BASE = `${BACKEND_URL}/api`;
export { API_BASE };

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  // Force HTTPS in production
  if (config.url && config.baseURL) {
    const fullUrl = config.baseURL + config.url;
    if (window.location.protocol === 'https:' && fullUrl.includes('http://')) {
      config.baseURL = config.baseURL.replace('http://', 'https://');
    }
  }
  
  const token = localStorage.getItem('bookvia-token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
// Flag to prevent cascading 401 handlers from wiping fallback session
// when multiple parallel requests fail with the same expired token.
let _handling401 = false;

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Don't redirect for login/auth endpoints - they handle their own 401 errors
      const requestUrl = error.config?.url || '';
      const isAuthEndpoint = requestUrl.includes('/auth/login') || 
                            requestUrl.includes('/auth/admin/login') ||
                            requestUrl.includes('/auth/business/login') ||
                            requestUrl.includes('/auth/business/manager-login') ||
                            requestUrl.includes('/auth/verify-email') ||
                            requestUrl.includes('/auth/resend-verification');
      
      if (!isAuthEndpoint) {
        // Guard 1: only ONE handler processes the auth failure. Others just reject.
        if (_handling401) return Promise.reject(error);

        // Guard 2: if the request was signed with a token that's already been
        // rotated away, this is a stale response — ignore it silently.
        const currentToken = localStorage.getItem('bookvia-token');
        const requestAuth = error.config?.headers?.Authorization || '';
        if (currentToken && requestAuth && !requestAuth.includes(currentToken)) {
          return Promise.reject(error);
        }

        _handling401 = true;
        // Clean up BOTH the legacy keys AND the multi-session store so we
        // don't leave a "ghost" active session pointing at an invalid token.
        try {
          const activeId = localStorage.getItem('bookvia-active-session-id');
          const raw = localStorage.getItem('bookvia-sessions');
          if (activeId && raw) {
            const sessions = JSON.parse(raw).filter(s => s.id !== activeId);
            localStorage.setItem('bookvia-sessions', JSON.stringify(sessions));
            if (sessions.length > 0) {
              // Fall back to another saved session instead of forcing a fresh login
              const next = sessions[0];
              localStorage.setItem('bookvia-token', next.token);
              localStorage.setItem('bookvia-user', JSON.stringify(next.user));
              if (next.business) localStorage.setItem('bookvia-business', JSON.stringify(next.business));
              else localStorage.removeItem('bookvia-business');
              if (next.manager) localStorage.setItem('bookvia-manager', JSON.stringify(next.manager));
              else localStorage.removeItem('bookvia-manager');
              localStorage.setItem('bookvia-active-session-id', next.id);
              window.location.href = '/'; // reload with fallback session
              return Promise.reject(error);
            }
          }
        } catch { /* fall through */ }

        // No other sessions → full logout
        localStorage.removeItem('bookvia-token');
        localStorage.removeItem('bookvia-user');
        localStorage.removeItem('bookvia-business');
        localStorage.removeItem('bookvia-manager');
        localStorage.removeItem('bookvia-sessions');
        localStorage.removeItem('bookvia-active-session-id');
        window.location.href = '/login';
      }
    }

    // Fase 10: backend signals that T&C are outdated AND grace period is
    // over. Fire a global event the TermsReAcceptModal listens for so the
    // user is asked to re-accept immediately.
    const detail = error.response?.data?.detail;
    if (error.response?.status === 409 && detail && typeof detail === 'object' && detail.code === 'terms_outdated') {
      try { window.dispatchEvent(new CustomEvent('bookvia:terms_outdated', { detail })); } catch { /* ignore */ }
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  unifiedLogin: (data) => api.post('/auth/unified-login', data),
  businessRegister: (data) => api.post('/auth/business/register', data),
  businessLogin: (data) => api.post('/auth/business/login', data),
  managerLogin: (data) => api.post('/auth/business/manager-login', data),
  getBusinessManagers: (email) => api.get(`/auth/business/managers?email=${encodeURIComponent(email)}`),
  getBusinessManagersByCode: (publicCode) => api.get(`/auth/business/managers?public_code=${encodeURIComponent(publicCode)}`),
  verifyEmail: (token) => api.get(`/auth/verify-email?token=${token}`),
  resendVerification: (data) => api.post('/auth/resend-verification', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  adminLogin: (data) => api.post('/auth/admin/login', data),
  googleSession: (data) => api.post('/auth/google/session', data),
  appleLogin: (data) => api.post('/auth/apple/login', data),
  createRegistrationSubscription: (data) => api.post('/auth/business/create-subscription', data),
  verifyRegistrationSubscription: (data) => api.post('/auth/business/verify-subscription', data),
  getMe: () => api.get('/auth/me'),
  sendPhoneCode: (phone) => api.post('/auth/phone/send-code', { phone }),
  verifyPhone: (phone, code) => api.post('/auth/phone/verify', { phone, code }),
  setup2FA: (password, tempToken = null) => {
    const config = tempToken ? { headers: { Authorization: `Bearer ${tempToken}` } } : {};
    return api.post('/auth/admin/setup-2fa', { password }, config);
  },
  verify2FA: (code, tempToken = null) => {
    const config = tempToken ? { headers: { Authorization: `Bearer ${tempToken}` } } : {};
    return api.post('/auth/admin/verify-2fa', { code }, config);
  },
};

// Users API
export const usersAPI = {
  updateProfile: (data) => api.put('/users/me', data),
  addFavorite: (businessId) => api.post(`/users/favorites/${businessId}`),
  removeFavorite: (businessId) => api.delete(`/users/favorites/${businessId}`),
  getFavorites: () => api.get('/users/favorites'),
  getMyStats: () => api.get('/users/my-stats'),
  getWallet: () => api.get('/users/me/wallet'),
  getWalletTransactions: (page = 1, limit = 20) => api.get('/users/me/wallet/transactions', { params: { page, limit } }),
  exportMyData: () => api.get('/users/me/export-data', {
    responseType: 'text',
    transformResponse: [(data) => data],
  }),
  setMarketingOptOut: (optOut) => api.post('/users/me/marketing-consent', { opt_out: optOut }),
  deleteAccount: (password, confirmation) => api.delete('/users/me/account', { data: { password, confirmation } }),
};

// Categories API
export const categoriesAPI = {
  getAll: (params = {}) => api.get('/categories', { params }),
  getBySlug: (slug) => api.get(`/categories/${slug}`),
  getSubcategories: (slugOrId) => api.get(`/categories/${slugOrId}/subcategories`),
  // NEW: server-computed 2-level tree with populares. Preferred entry point
  // for browse screens. Falls back gracefully when the endpoint isn't
  // deployed yet (see the callers).
  getGroupedTree: (params = {}) => api.get('/categories/grouped/tree', { params }),
  create: (data) => api.post('/categories', data),
};

// Businesses API
export const businessesAPI = {
  search: (params) => api.get('/businesses', { params }),
  getFeatured: (limit = 8, country_code, city) => api.get('/businesses/featured', { params: { limit, ...(country_code ? { country_code } : {}), ...(city ? { city } : {}) } }),
  getBySlug: (slug) => api.get(`/businesses/slug/${slug}`),
  getById: (id) => api.get(`/businesses/${id}`),
  getTrustScore: (id) => api.get(`/businesses/${id}/trust-score`),
  updateMe: (data) => api.put('/businesses/me', data),
  getDashboard: (params) => api.get('/businesses/me/dashboard', { params }),
  getPrivateInfo: () => api.get('/businesses/me/private-info'),
  updateLegalDocs: (data) => api.put('/businesses/me/legal-docs', data),
  resubmitDocuments: (data) => api.post('/businesses/me/resubmit-documents', data),
  // Signed URL for viewing sensitive legal documents (INE, Constancia, CLABE)
  // Backend verifies caller is admin or business owner before returning a
  // short-lived Cloudinary URL.
  getSignedDocumentUrl: (businessId, docType) =>
    api.get(`/documents/business/${businessId}/${docType}`),
  getBusinessHours: () => api.get('/businesses/me/hours'),
  updateBusinessHours: (hours) => api.put('/businesses/me/hours', hours),
  // Workers (for specific business - public)
  getWorkers: (businessId, includeInactive = false, serviceId = null) => 
    api.get(`/businesses/${businessId}/workers`, { params: { include_inactive: includeInactive, ...(serviceId ? { service_id: serviceId } : {}) } }),
  // Workers (for authenticated business)
  getMyWorkers: (includeInactive = false) => 
    api.get('/businesses/my/workers', { params: { include_inactive: includeInactive } }),
  getWorker: (workerId) => api.get(`/businesses/my/workers/${workerId}`),
  createWorker: (data) => api.post('/businesses/my/workers', data),
  updateWorker: (workerId, data) => api.put(`/businesses/my/workers/${workerId}`, data),
  deleteWorker: (workerId) => api.delete(`/businesses/my/workers/${workerId}`),
  uploadWorkerPhoto: (workerId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/businesses/my/workers/${workerId}/photo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  reactivateWorker: (workerId) => api.put(`/businesses/my/workers/${workerId}/reactivate`),
  updateWorkerServices: (workerId, serviceIds) => api.put(`/businesses/my/workers/${workerId}/services`, { service_ids: serviceIds }),
  updateWorkerSchedule: (workerId, schedule) => 
    api.put(`/businesses/my/workers/${workerId}/schedule`, { schedule }),
  addWorkerException: (workerId, exception) => 
    api.post(`/businesses/my/workers/${workerId}/exceptions`, { exception }),
  removeWorkerException: (workerId, exceptionId) => 
    api.delete(`/businesses/my/workers/${workerId}/exceptions/${exceptionId}`),
  // Photos
  uploadPhoto: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/businesses/me/photos', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletePhoto: (photoId) => api.delete(`/businesses/me/photos/${photoId}`),
  getMyPhotos: () => api.get('/businesses/me/photos'),
  uploadLogo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/businesses/me/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteLogo: () => api.delete('/businesses/me/logo'),
  uploadCover: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/businesses/me/cover', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteCover: () => api.delete('/businesses/me/cover'),
  // Closures (closed days)
  getMyClosures: () => api.get('/businesses/me/closures'),
  addClosure: (date, reason) => api.post('/businesses/me/closures', { date, reason }),
  removeClosure: (date) => api.delete(`/businesses/me/closures/${date}`),
  // Subscription
  createSubscription: (originUrl) => api.post('/businesses/me/subscribe', { origin_url: originUrl }),
  getSubscriptionStatus: (sessionId) => api.get(`/businesses/me/subscription/status${sessionId ? `?session_id=${sessionId}` : ''}`),
  cancelSubscription: () => api.post('/businesses/me/subscription/cancel'),
  billingPortal: () => api.post('/businesses/me/subscription/billing-portal'),
  // Stripe Connect Express (Phase A)
  connectOnboard: () => api.post('/stripe-connect/onboard'),
  connectStatus: () => api.get('/stripe-connect/status'),
  connectDashboardLink: () => api.post('/stripe-connect/dashboard-link'),
  // Phase G — Winback campaigns & privacy (LFPDPPP)
  adminInactiveUsers: (params) => api.get('/admin/winback/inactive-users', { params }),
  adminRunWinbackCampaign: (data) => api.post('/admin/winback/campaign', data),
  adminWinbackHistory: () => api.get('/admin/winback/campaigns'),
  unsubscribeInfo: (token) => api.get('/users/unsubscribe-info', { params: { token } }),
  unsubscribe: (token) => api.post('/users/unsubscribe', { token }),
  deleteMyAccount: () => api.post('/users/me/delete-account'),
  deleteMyBusinessAccount: () => api.post('/users/me/business/delete-account'),
  // Blacklist
  getBlacklist: () => api.get('/businesses/me/blacklist'),
  addToBlacklist: (data) => api.post('/businesses/me/blacklist', data),
  removeFromBlacklist: (entryId) => api.delete(`/businesses/me/blacklist/${entryId}`),
  // Owner PIN
  setOwnerPin: (pin) => api.post('/businesses/me/pin', { pin }),
  verifyOwnerPin: (pin) => api.post('/businesses/me/pin/verify', { pin }),
  getPinStatus: () => api.get('/businesses/me/pin/status'),
  // Manager
  designateManager: (workerId, permissions) => api.put(`/businesses/my/workers/${workerId}/manager`, { permissions }),
  removeManager: (workerId) => api.delete(`/businesses/my/workers/${workerId}/manager`),
  updateManagerPermissions: (workerId, permissions) => api.put(`/businesses/my/workers/${workerId}/manager/permissions`, { permissions }),
  setManagerPin: (workerId, pin) => api.post(`/businesses/my/workers/${workerId}/manager/pin`, { pin }),
  getActivityLog: (params = {}) => api.get('/businesses/my/activity-log', { params }),
  getReports: (period = 'month') => api.get('/businesses/my/reports', { params: { period } }),
  exportReports: (period = 'month') => api.get('/businesses/my/reports/export', { params: { period }, responseType: 'blob' }),
  getDashboardSummary: () => api.get('/businesses/my/dashboard-summary'),
  getProfileCompletion: () => api.get('/businesses/my/profile-completion'),
  listMyClients: (params = {}) => api.get('/businesses/my/clients', { params }),
  updateClientNote: (clientKey, note) => api.put(`/businesses/my/clients/${encodeURIComponent(clientKey)}/note`, { note }),
  exportMyClients: () => api.post('/businesses/my/clients/export', null, { responseType: 'blob' }),
  lookupClientByCode: (code) => api.get('/businesses/my/clients/lookup', { params: { code } }),
  acceptCommissionTerms: (data) => api.post('/businesses/me/commission-terms/accept', data),
  updateTaxRegime: (taxRegime, certificateUrl) => api.put('/businesses/me/tax-regime', { tax_regime: taxRegime, tax_regime_certificate_url: certificateUrl || null }),
  downloadLegalFile: () => api.get('/businesses/me/legal-file.pdf', { responseType: 'blob' }),
  verifyLegalFile: (fileId) => api.get(`/businesses/verificar-expediente/${fileId}`),
  listMySettlements: (limit = 24) => api.get('/businesses/me/settlements', { params: { limit } }),
  downloadSettlementStatement: (settlementId) =>
    api.get(`/businesses/me/settlements/${settlementId}/statement.pdf`, { responseType: 'blob' }),
  getClientHistory: (userId) => api.get(`/businesses/my/client-history/${userId}`),
  getMyBusiness: () => api.get('/businesses/me'),
  updateBusiness: (data) => api.put('/businesses/me', data),
  uploadPublicFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/public', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Services API
export const servicesAPI = {
  getByBusiness: (businessId) => api.get(`/services/business/${businessId}`),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
};

// Bookings API
export const bookingsAPI = {
  getAvailability: (businessId, date, serviceId, workerId, includeUnavailable = false) => 
    api.get(`/bookings/availability/${businessId}`, { 
      params: { 
        date, 
        service_id: serviceId,
        worker_id: workerId,
        include_unavailable: includeUnavailable
      } 
    }),
  create: (data) => api.post('/bookings', data),
  getMy: (params) => api.get('/bookings/my', { params }),
  getBusiness: (params) => api.get('/bookings/business', { params }),
  cancelByUser: (id, reason, refundTo = "card") => api.put(`/bookings/${id}/cancel/user`, { reason, refund_to: refundTo }),
  cancelByBusiness: (id, reason) => api.put(`/bookings/${id}/cancel/business`, { reason }),
  getCancellationPreview: (id) => api.get(`/bookings/${id}/cancellation-preview`),
  raiseDispute: (id, reason) => api.post(`/bookings/${id}/dispute`, { reason }),
  confirmOk: (id) => api.post(`/bookings/${id}/confirm-ok`),
  refundChoice: (id, destination) => api.post(`/bookings/${id}/refund-choice`, { destination }),
  reportNoShow: (id, description, photoUrl) => api.post(`/bookings/${id}/no-show-business`, { description, photo_url: photoUrl }),
  respondNoShow: (id, description, evidenceUrl) => api.post(`/bookings/${id}/no-show-response`, { description, evidence_url: evidenceUrl }),
  reschedule: (id, newDate, newTime) => 
    api.put(`/bookings/${id}/reschedule`, null, { params: { new_date: newDate, new_time: newTime } }),
  rescheduleByBusiness: (id, data) => api.put(`/bookings/${id}/reschedule/business`, data),
  getAvailability: (businessId, date, serviceId, workerId) => 
    api.get(`/bookings/availability/${businessId}`, { params: { date, service_id: serviceId, worker_id: workerId } }),
  confirm: (id) => api.put(`/bookings/${id}/confirm`),
  complete: (id) => api.put(`/bookings/${id}/complete`),
  markNoShow: (id) => api.put(`/bookings/${id}/no-show`),
  getStatsDetail: (statType, dateFrom, dateTo, branchId) => api.get('/bookings/business/stats-detail', { 
    params: { stat_type: statType, date_from: dateFrom, date_to: dateTo, branch_id: branchId || undefined } 
  }),
  searchClients: (q) => api.get('/bookings/search-clients', { params: { q } }),
};

// Reviews API
export const reviewsAPI = {
  create: (data) => api.post('/reviews/', data),
  getByBusiness: (businessId, params) => api.get(`/reviews/business/${businessId}`, { params }),
  report: (reviewId, reason, detail) => api.post(`/reviews/${reviewId}/report`, { reason, detail: detail || null }),
  adminListReported: (status_filter = 'pending', limit = 50) => api.get('/reviews/admin/reported', { params: { status_filter, limit } }),
  adminResolve: (reviewId, action, note) => api.post(`/reviews/admin/${reviewId}/resolve`, { action, note: note || null }),
};

// Payments API
export const paymentsAPI = {
  // Deposit checkout for bookings.
  // `platform` is set to "native" automatically when running inside the
  // Capacitor shell so the backend can add `&native=1` to success/cancel
  // URLs (used by the deep-link auto-return to the native app).
  createDepositCheckout: (bookingId, useWallet = false) => {
    let platform = null;
    try {
      // Lazy check — avoids Capacitor being required on plain-web tests.
      const { Capacitor } = require('@capacitor/core');
      if (Capacitor?.isNativePlatform?.()) platform = 'native';
    } catch { /* ignore */ }
    return api.post('/payments/deposit/checkout', {
      booking_id: bookingId,
      use_wallet: useWallet,
      platform,
    });
  },
  getCheckoutStatus: (sessionId) => api.get(`/payments/checkout/status/${sessionId}`),
  getTransaction: (transactionId) => api.get(`/payments/transaction/${transactionId}`),
  getMyTransactions: (params) => api.get('/payments/my-transactions', { params }),
  getBusinessTransactions: (params) => api.get('/payments/business-transactions', { params }),
  getFeesBreakdown: (depositAmount) => api.get('/payments/fees/breakdown', { params: { deposit_amount: depositAmount } }),
  // Legacy checkout for subscriptions
  createCheckoutSession: (data) => api.post('/payments/checkout/session', data),
  // Saved cards (1-tap re-booking)
  listSavedCards: () => api.get('/payments/saved-cards'),
  deleteSavedCard: (paymentMethodId) => api.delete(`/payments/saved-cards/${paymentMethodId}`),
  setDefaultSavedCard: (paymentMethodId) => api.post('/payments/saved-cards/default', { payment_method_id: paymentMethodId }),
  payBookingWithSavedCard: (bookingId, paymentMethodId, walletCreditToApply = null) =>
    api.post('/payments/pay-booking-with-saved-card', {
      booking_id: bookingId,
      payment_method_id: paymentMethodId,
      wallet_credit_to_apply: walletCreditToApply,
    }),
};

// Business Finance API
export const financeAPI = {
  getSummary: () => api.get('/business/finance/summary'),
  getTransactions: (params) => api.get('/business/finance/transactions', { params }),
  getFundsState: () => api.get('/business/finance/funds-state'),
  getStrikes: () => api.get('/business/finance/strikes'),
  getLedger: (params) => api.get('/business/finance/ledger', { params }),
  getSettlements: (params) => api.get('/business/finance/settlements', { params }),
};

// Notifications API
export const notificationsAPI = {
  getAll: (unreadOnly = false) => api.get('/notifications', { params: { unread_only: unreadOnly } }),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
};

// Admin API
export const termsAPI = {
  getVersion: () => api.get('/terms/version'),
  myStatus: () => api.get('/terms/me'),
  accept: (version) => api.post('/terms/accept', { version }),
};

export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getPendingBusinesses: () => api.get('/admin/businesses/pending'),
  getAllBusinesses: (params) => api.get('/admin/businesses/all', { params }),
  getAllUsers: (params) => api.get('/admin/users/all', { params }),
  approveBusiness: (id) => api.put(`/admin/businesses/${id}/approve`),
  rejectBusiness: (id, reason) => api.put(`/admin/businesses/${id}/reject`, null, { params: { reason } }),
  suspendBusiness: (id, reason) => api.put(`/admin/businesses/${id}/suspend`, null, { params: { reason } }),
  verifyBusinessDocuments: (id) => api.post(`/admin/businesses/${id}/verify-documents`),
  rejectBusinessDocuments: (id, reason) => api.post(`/admin/businesses/${id}/reject-documents`, { reason }),
  requestBusinessRevision: (id, body) => api.post(`/admin/businesses/${id}/request-revision`, body),
  getPendingDocsBusinesses: (limit = 50) => api.get('/admin/businesses/pending-docs', { params: { limit } }),
  suspendUser: (id, days, reason) => api.put(`/admin/users/${id}/suspend`, null, { params: { days, reason } }),
  deleteReview: (id, reason) => api.delete(`/admin/reviews/${id}`, { params: { reason } }),
  getAuditLogs: (params) => api.get('/admin/audit-logs', { params }),
  toggleFeatured: (id, featured) => api.put(`/admin/businesses/${id}/feature`, null, { params: { featured } }),
  getSentEmails: (params) => api.get('/admin/emails', { params }),
  holdPayment: (id, reason) => api.put(`/admin/payments/${id}/hold`, null, { params: { reason } }),
  releasePayment: (id) => api.put(`/admin/payments/${id}/release`),
  getHeldPayments: (params) => api.get('/admin/payments/held', { params }),
  getSettlements: (params) => api.get('/admin/settlements', { params }),
  generateSettlements: (year, month) => api.post(`/admin/settlements/generate?year=${year}&month=${month}`),
  generateDay20Settlements: (force = false) => api.post(`/admin/settlements/generate-day20${force ? '?force=true' : ''}`),
  exportSpeiCsv: (periodKey, statusFilter = 'pending', bank = 'generic') => api.get(`/admin/settlements/${periodKey}/export-spei.csv`, {
    params: { status_filter: statusFilter, bank },
    responseType: 'text',
    transformResponse: [(data) => data],
  }),
  markSettlementPaid: (id, payout_reference) => api.put(`/admin/settlements/${id}/pay`, { payout_reference }),
  togglePayoutHold: (businessId, hold, reason) => api.put(`/admin/businesses/${businessId}/payout-hold`, { hold, reason }),
  exportTransactions: (year, month) => api.get(`/admin/export/transactions?year=${year}&month=${month}`, { responseType: 'blob' }),
  exportSettlements: (year, month) => api.get(`/admin/export/settlements?year=${year}&month=${month}`, { responseType: 'blob' }),
  getBusinessDetail: (id) => api.get(`/admin/businesses/${id}/detail`),
  getAllReviews: (params) => api.get('/admin/reviews/all', { params }),
  getSubscriptions: () => api.get('/admin/subscriptions'),
  getGrowthStats: (months) => api.get('/admin/growth', { params: { months } }),
  // Categories CRUD
  getCategories: () => api.get('/admin/categories'),
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (id, data) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/categories/${id}`),
  // Platform config
  getConfig: () => api.get('/admin/config'),
  updateConfig: (data) => api.put('/admin/config', data),
  // Support tickets
  getTickets: (params) => api.get('/admin/tickets', { params }),
  getTicketStats: () => api.get('/admin/tickets/stats'),
  getTicketDetail: (id) => api.get(`/admin/tickets/${id}`),
  respondToTicket: (id, message) => api.post(`/admin/tickets/${id}/respond`, { message }),
  closeTicket: (id) => api.put(`/admin/tickets/${id}/close`),
  // Rankings
  getRankings: () => api.get('/admin/rankings'),
  // Alerts
  getAlerts: () => api.get('/admin/alerts'),
  // Cities
  getCities: (params) => api.get('/admin/cities', { params }),
  toggleCity: (slug, active) => api.put(`/admin/cities/${slug}/toggle`, null, { params: { active } }),
  // Custom reports
  getCustomReport: (params) => api.get('/admin/reports/custom', { params }),
  // Staff management
  getStaff: () => api.get('/admin/staff'),
  createStaff: (data) => api.post('/admin/staff', data),
  updateStaff: (id, data) => api.put(`/admin/staff/${id}`, data),
  deleteStaff: (id) => api.delete(`/admin/staff/${id}`),
  resetStaffPassword: (id) => api.put(`/admin/staff/${id}/reset-password`),
  getMyPermissions: () => api.get('/admin/my-permissions'),
  reassignCategory: (bizId, categoryId) => api.put(`/admin/businesses/${bizId}/reassign-category`, null, { params: { category_id: categoryId } }),
  // Phase 12: P&L, reconciliation, security, compliance, refunds, webhook events
  getPlatformPnl: (days = 30) => api.get('/admin/platform/pnl', { params: { days } }),
  runStripeReconciliation: (date) => api.post('/admin/platform/reconcile-stripe', null, { params: date ? { date } : {} }),
  getReconciliationIssues: (limit = 50) => api.get('/admin/platform/reconciliation-issues', { params: { limit } }),
  getLockedAccounts: () => api.get('/admin/security/locked-accounts'),
  unlockAccount: (key) => api.post('/admin/security/unlock', { key }),
  getTermsStats: () => api.get('/admin/terms/stats'),
  getTermsPendingUsers: (limit = 50) => api.get('/admin/terms/pending-users', { params: { limit } }),
  getArcoEvents: (limit = 50) => api.get('/admin/compliance/arco-events', { params: { limit } }),
  getRefundsAudit: (limit = 50) => api.get('/admin/finance/refunds', { params: { limit } }),
  getRefundsPending: (limit = 100) => api.get('/admin/finance/refunds/pending', { params: { limit } }),
  getRefundsAwaitingChoice: (limit = 100) => api.get('/admin/finance/refunds/awaiting-choice', { params: { limit } }),
  getRefundsWalletHistory: (limit = 200) => api.get('/admin/finance/refunds/wallet-refunded', { params: { limit } }),
  issueRefund: (transactionId) => api.post(`/admin/finance/refunds/${transactionId}/issue`),
  issueAllRefunds: () => api.post('/admin/finance/refunds/issue-all'),
  retryRefund: (transactionId) => api.post(`/admin/finance/refunds/${transactionId}/retry`),
  getStripeWebhookEvents: (limit = 50) => api.get('/admin/stripe/webhook-events', { params: { limit } }),
  previewPnlReport: () => api.get('/admin/platform/pnl-report/preview'),
  sendPnlReport: (recipients) => api.post('/admin/platform/pnl-report/send', { recipients: recipients || null }),
  getWaitlist: (params = {}) => api.get('/admin/waitlist', { params }),
  exportWaitlist: (params = {}) => api.get('/admin/waitlist/export', { params, responseType: 'blob' }),
  deleteWaitlistEntry: (id) => api.delete(`/admin/waitlist/${id}`),
  previewWaitlistBroadcast: (city, country_code = 'MX', only_unnotified = true) =>
    api.get(`/admin/waitlist/cities/${encodeURIComponent(city)}/preview`, { params: { country_code, only_unnotified } }),
  sendWaitlistBroadcast: (body) => api.post('/admin/waitlist/broadcast', body),
  // Phase I — Business QR codes
  getQrBusinesses: (params = {}) => api.get('/admin/qr/businesses', { params }),
  getQrScansSummary: (days = 30) => api.get('/admin/qr/scans/summary', { params: { days } }),
  // Bookings history
  listBookings: (params = {}) => api.get('/admin/bookings', { params }),
  getBookingDetail: (id) => api.get(`/admin/bookings/${id}`),
};

// Utility API
export const utilityAPI = {
  getCities: (countryCode = 'MX') => api.get('/cities', { params: { country_code: countryCode } }),
  seed: () => api.post('/seed'),
  seedCountries: () => api.post('/seed/countries'),
  createSupportTicket: (data) => api.post('/support/tickets', data),
  getMyTickets: (params) => api.get('/support/my-tickets', { params }),
  getPlatformStats: () => api.get('/platform-stats'),
};

// SEO API
export const seoAPI = {
  getCountries: () => api.get('/seo/countries'),
  getCities: (countryCode) => api.get(`/seo/cities/${countryCode}`),
  getCategories: () => api.get('/seo/categories'),
  getMeta: (pageType, slug, country = 'mx', city = null) => 
    api.get(`/seo/meta/${pageType}/${slug}`, { params: { country, city } }),
  getBusinesses: (country, city, category = null, page = 1) => 
    api.get(`/seo/businesses/${country}/${city}`, { params: { category, page } }),
  getBusiness: (country, city, slug) => 
    api.get(`/seo/business/${country}/${city}/${slug}`),
};

// Push Notifications preferences
export const pushAPI = {
  getPreferences: () => api.get('/push/preferences'),
  updatePreferences: (preferences) => api.patch('/push/preferences', { preferences }),
};

export default api;
